import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import AdminShiftTable from '../../components/AdminShiftTable/AdminShiftTable.js';
import AdminDrawer from '../../components/AdminDrawer/AdminDrawer';
import { AdminContainer, ContentBox } from './AdminShiftView.styles';
import { fetchEmployees } from '../../redux/actions/employeesActions';
import { fetchShiftTypes } from '../../redux/actions/shiftTypesActions';
import { selectCurrentVenue } from '../../redux/slices/currentUserSlice';
import { getWeekBoundaries } from '../../utils/dateUtils';
import {
  setSelectedPeriod,
  selectPeriod,
  startVenueShiftsListener,
  stopVenueShiftsListener,
  selectSelectedShift,
  setSelectedShift,
} from '../../redux/shifts/shiftsSlice';
import { useDrawer } from '../../utils/Hooks/drawer/useDrawer';
import { DRAWER_CONFIG, DRAWER_TYPES } from './adminShiftDrawerConfig';
import { useVenueTimeFormatters } from '../../utils/Hooks/venue/useVenueTimeFormatters';

const AdminViewShift = () => {
  const dispatch = useDispatch();
  const venue = useSelector(selectCurrentVenue);
  const period = useSelector(selectPeriod);
  const selectedShift = useSelector(selectSelectedShift);
  const { formatDate } = useVenueTimeFormatters();
  const vid = venue?.id;
  const startOfWeekIndex = venue?.startOfWeekIndex ?? 0;
  const prevDrawerTypeRef = useRef(null);

  // Initialize drawer system
  const {
    isDrawerOpen,
    drawerType,
    drawerData,
    drawerTitle,
    handleOpenDrawer,
    handleCloseDrawer,
    handleBackNavigation,
    renderDrawerContent,
  } = useDrawer({ DRAWER_CONFIG }, null, false);

  useEffect(() => {
    dispatch(fetchEmployees());
    dispatch(fetchShiftTypes());
  }, [dispatch, vid]);

  // Initialize selected period if not set
  useEffect(() => {
    if (vid && (!period.start || !period.end)) {
      const { weekStart, weekEnd } = getWeekBoundaries(new Date(), startOfWeekIndex);
      dispatch(
        setSelectedPeriod({
          start: weekStart.toISOString(),
          end: weekEnd.toISOString(),
        })
      );
    }
  }, [vid, period, startOfWeekIndex, dispatch]);

  // Use the Redux thunk to manage the real-time listener
  useEffect(() => {
    if (vid && period.start && period.end) {
      dispatch(startVenueShiftsListener(vid, period));
      return () => {
        dispatch(stopVenueShiftsListener());
      };
    }
  }, [dispatch, vid, period]);

  // Clear selectedShift when opening ADD_SHIFT drawer to start fresh
  // This ensures we don't show a shift date when initially opening the add shift form
  useEffect(() => {
    if (drawerType === DRAWER_TYPES.ADD_SHIFT && isDrawerOpen) {
      // Only clear if we're transitioning to ADD_SHIFT (not already in ADD_SHIFT)
      if (prevDrawerTypeRef.current !== DRAWER_TYPES.ADD_SHIFT) {
        dispatch(setSelectedShift(null));
      }
    }
    prevDrawerTypeRef.current = drawerType;
  }, [drawerType, isDrawerOpen, dispatch]);

  // Close drawer when selectedShift becomes null (e.g., after deletion)
  useEffect(() => {
    if (!selectedShift && isDrawerOpen && drawerType === DRAWER_TYPES.SHIFT_INFORMATION) {
      handleCloseDrawer();
    }
  }, [selectedShift, isDrawerOpen, drawerType, handleCloseDrawer]);

  // Helper function to generate drawer title
  const getDrawerTitle = () => {
    if (drawerType === DRAWER_TYPES.ADD_SHIFT) {
      // If a shift was created, show the shift date
      if (selectedShift?.start) {
        return formatDate(selectedShift.start, { month: 'short', day: 'numeric' });
      }
      return 'Add New Shift';
    }
    if (drawerType === DRAWER_TYPES.SHIFT_INFORMATION) {
      const shift = drawerData?.shift || selectedShift;
      return shift?.start ? formatDate(shift.start, { month: 'short', day: 'numeric' }) : 'Shift Details';
    }
    return drawerTitle;
  };

  return (
    <AdminContainer>
      <ContentBox className={isDrawerOpen ? 'drawer-open' : ''}>
        <AdminShiftTable onOpenDrawer={handleOpenDrawer} isDrawerOpen={isDrawerOpen} />
      </ContentBox>

      <AdminDrawer
        isOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
        onBack={drawerType && DRAWER_CONFIG[drawerType]?.parent ? handleBackNavigation : null}
        title={getDrawerTitle()}
      >
        {renderDrawerContent()}
      </AdminDrawer>
    </AdminContainer>
  );
};

export default AdminViewShift;
